import { sweepMovement, type Terrain, type Vec2 } from '../terrain.js';
import { PLAYER_RADIUS_M } from './positioning.js';

/** One geometry authority for common and skill dashes; never mutates the world. */
export interface DashMovementResult {
  readonly start: Vec2;
  readonly landed: Vec2;
  readonly requestedM: number;
  readonly actualM: number;
  /** Exact untraveled portion of the terrain sweep, for skill wall-impact metrics. */
  readonly remainingDistanceM: number;
  readonly collided: boolean;
}

/**
 * Returns null for an invalid request instead of silently clamping forged data.
 * Only terrain participates in the sweep: monster bodies cannot block a dash,
 * and invulnerability or wall phasing is never implied by this movement.
 */
export function resolveDashMovement(
  terrain: Terrain,
  origin: Vec2,
  heading: Vec2,
  requestedDistanceM: number,
  maxDistanceM: number,
): DashMovementResult | null {
  if (!Number.isFinite(maxDistanceM) || maxDistanceM <= 0 ||
      !Number.isFinite(requestedDistanceM) || requestedDistanceM <= 0 || requestedDistanceM > maxDistanceM ||
      !Number.isFinite(origin.x) || !Number.isFinite(origin.y) ||
      !Number.isFinite(heading.x) || !Number.isFinite(heading.y)) return null;
  const magnitude = Math.hypot(heading.x, heading.y);
  if (!Number.isFinite(magnitude) || magnitude <= 1e-9) return null;
  const swept = sweepMovement(terrain, origin, {
    x: heading.x / magnitude * requestedDistanceM,
    y: heading.y / magnitude * requestedDistanceM,
  }, PLAYER_RADIUS_M);
  return {
    start: { ...origin }, landed: { ...swept.position }, requestedM: requestedDistanceM,
    actualM: swept.actualDistanceM, remainingDistanceM: swept.remainingDistanceM,
    collided: swept.collision !== null,
  };
}
